import { useMutation } from "@tanstack/react-query";
import type { Question } from "@/models/question";
import services from "@/services";
import { filterPage } from "@/utils/filter";
import { flatMapQuestions } from "@/utils/questions";
import { SubmitError } from "@/utils/submitError";
import { mapQuestionToAnswers } from "@/utils/surveyConverter";
import type { SurveyTakingFormValues } from "../[surveyId]/_components/SurveyTaking";
type GuestData = {
  email: string;
  firstname: string;
  lastname: string;
  sessions: string[];
  custom_fields: unknown[];
  app_from: string;
  is_owner: number;
};
type SubmitArgs = {
  surveyId: string;
  entryId?: string;
  values: SurveyTakingFormValues;
  isPreviewMode?: boolean;
};
type SubmitResult = {
  entryId?: string;
} | undefined;
type ServerResponseItem = {
  message?: string;
  headers?: {
    entry?: string;
  };
  guests?: string[] | unknown;
};
export const useSubmitAnswersMutation = () => {
  return useMutation<SubmitResult, SubmitError, SubmitArgs>({
    mutationFn: async ({
      surveyId,
      values,
      isPreviewMode,
      entryId
    }) => {
      if (isPreviewMode) {
        return {
          entryId: undefined
        };
      }
      const guests: GuestData[] = [];
      const entry = {
        survey_id: surveyId,
        entry_id: entryId,
        email: values?.authentication_form?.email,
        firstname: values?.authentication_form?.firstName,
        lastname: values?.authentication_form?.lastName,
        answers: [] as unknown[],
        startTimestamp: values.startTimestamp,
        endTimestamp: values.endTimestamp
      };
      const questions: Question[] = values.pages.filter(filterPage).reduce<Question[]>((acc, p) => [...acc, ...flatMapQuestions(p.questions as Question[])], []);
      for (const question of questions) {
        if (!question.is_visible || !question.question_type?.slug) continue;
        if (question.question_type.slug === "participant-block") {
          question.participant?.forEach((participant, i) => {
            guests.push({
              email: participant.email,
              firstname: participant.firstName,
              lastname: participant.lastName,
              sessions: participant.sessions,
              custom_fields: participant.questions?.flatMap(mapQuestionToAnswers) ?? [],
              app_from: "questionnaire",
              is_owner: i === 0 ? 1 : 0
            });
          });
        } else {
          entry.answers.push(...mapQuestionToAnswers(question));
        }
      }
      const formattedData = {
        entries: [{
          entry: {
            survey_id: entry.survey_id,
            ...(entry.entry_id ? {
              entry_id: entry.entry_id
            } : {}),
            answers: entry.answers,
            email: entry.email,
            firstname: entry.firstname,
            lastname: entry.lastname,
            ended_at: entry.endTimestamp,
            ...(entry.startTimestamp ? {
              started_at: entry.startTimestamp
            } : {})
          },
          guests: guests.map(g => ({
            email: g.email,
            firstname: g.firstname,
            lastname: g.lastname,
            sessions: g.sessions,
            custom_fields: g.custom_fields,
            app_from: g.app_from,
            is_owner: g.is_owner
          }))
        }]
      };
      try {
        const response: ServerResponseItem[] = await services.survey.persistAnswers(formattedData);
        const data = response?.[0];
        if (!data) {
          throw new SubmitError("unexpected_response", 500);
        }
        if (data.message === "session.alreadyRegistered") {
          const entryId_0 = data.headers?.entry;
          throw new SubmitError("session.alreadyRegistered", 403, entryId_0);
        }
        if (data.message === "session.isFull") {
          throw new SubmitError("session.isFull", 422);
        }
        if (Array.isArray(data.guests) && data.guests[0] === "question.notLinked") {
          throw new SubmitError("question.notLinked", 422);
        }
        if (data.message === "participant.limitReached") {
          throw new SubmitError("participant.limitReached", 422);
        }
        return {
          entryId: data.headers?.entry
        };
      } catch (error) {
        if (error instanceof SubmitError) throw error;
        const messageKey = (error as {
          response?: {
            data?: {
              message?: string;
            };
          };
        })?.response?.data?.message ?? "error";
        const statusCode = (error as {
          response?: {
            status?: number;
          };
        })?.response?.status ?? 500;
        throw new SubmitError(String(messageKey), statusCode, undefined, error);
      }
    }
  });
};